import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"

import { SuccessContext } from "../../../components/layout/success-snackbar"
import { Children } from "../../../components/miscellianous/children"
import { Project } from "../../dto/project/Project"
import { ProjectCreationDto } from "../../dto/project/ProjectCreationDto"
import { ProjectUpdateDto } from "../../dto/project/ProjectUpdateDto"
import { CodesVersion } from "../../enum/codesVersionEnum"
import { ProjectStatusEnum } from "../../enum/projectStatusEnum"
import { useProject } from "../../hooks/projects/use-projects"
import { CodeReferenceContextProvider } from "../code-acv/CodeReferenceContext"
import { OrganizationContext } from "../organization/organization-context"
import { ProjectCountersContextProvider } from "./ProjectCountersContext"
import { ProjectListContext } from "./ProjectListContext"

export interface IForm {
  name: string
  businessCode: string
  licenceDate?: Date
  street: string
  city: string
  additional: string
  status: ProjectStatusEnum
  departmentId: string

  seismicZone: boolean
  folder: string
}

export function formToCreationDto(form: IForm): ProjectCreationDto {
  return {
    name: form.name,
    businessCode: form.businessCode,
    licenceDate: form.licenceDate,
    address: {
      street: form.street?.trim(),
      city: form.city?.trim(),
      additional: form.additional?.trim(),
    },
    departmentId: form.departmentId,
    seismicZone: form.seismicZone,
    folder: form.folder,
  }
}

export function formToUpdateDto(project: Project, form?: IForm): ProjectUpdateDto {
  return form
    ? {
        id: project.id ?? "",
        organizationId: project.organizationId,
        name: form.name,
        businessCode: form.businessCode,
        licenceDate: form.licenceDate,
        address: {
          street: form.street,
          city: form.city,
          additional: form.additional,
        },
        codesVersion: project.codesVersion,
        status: form.status,
        coverImgFileName: project.coverImgFileName,
        coverImgFileType: project.coverImgFileType,
        departmentId: form.departmentId,
        seismicZone: form.seismicZone,
        folder: form.folder,
      }
    : {
        id: project.id ?? "",
        organizationId: project.organizationId,
        name: project.name,
        businessCode: project.businessCode,
        licenceDate: project.licenceDate,
        address: {
          street: project.address?.street,
          city: project.address?.city,
          additional: project.address?.additional,
        },
        codesVersion: project.codesVersion,
        status: project.status,
        coverImgFileName: project.coverImgFileName,
        coverImgFileType: project.coverImgFileType,
        departmentId: project.departmentId,
        seismicZone: project.seismicZone,
        folder: project.folder,
      }
}

export function dtoToForm(project: Project | undefined): IForm {
  return project
    ? {
        name: project.name,
        businessCode: project.businessCode,
        licenceDate: project.licenceDate,
        street: project.address?.street,
        city: project.address?.city,
        additional: project.address?.additional,
        status: project.status,
        departmentId: project.departmentId,
        seismicZone: project.seismicZone,
        folder: project.folder,
      }
    : {
        name: "",
        businessCode: "",
        licenceDate: new Date(),
        street: "",
        city: "",
        additional: "",
        status: ProjectStatusEnum.IN_PROGRESS,
        departmentId: "",
        seismicZone: false,
        folder: "",
      }
}

export const ProjectContext = React.createContext<ProjectStore>({} as ProjectStore)

export function ProjectContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const openSuccessSnackbar = useContext(SuccessContext)
  const { refreshOrganization } = useContext(OrganizationContext)
  const { refreshAllProjectCard } = useContext(ProjectListContext)

  const { projectId } = useParams()
  const { createProject, getProject, updateProject, updateProjectStatus } = useProject()

  const [project, setProject] = useState<Project | undefined>()
  const [isDeleting, setIsDeleting] = useState<boolean>(false)

  const createNewProject: (projectCreationDto: ProjectCreationDto) => Promise<Project> = useCallback(
    (projectCreationDto) =>
      createProject(projectCreationDto)
        .then((newProject) => {
          setProject(newProject)
          return newProject
        })
        .then((newProject) => {
          refreshAllProjectCard()
          return newProject
        }),
    [createProject, refreshAllProjectCard]
  )

  const fetchProject: (id: string) => Promise<void> = useCallback(
    (id) =>
      getProject(id).then((fetchedProject) => {
        if (fetchedProject) {
          setProject(fetchedProject)
        }
      }),
    [getProject]
  )

  const updateActualProject: (projectUpdateDto: ProjectUpdateDto) => Promise<any> = useCallback(
    (projectUpdateDto: ProjectUpdateDto) => {
      if (projectId && projectId !== "new") {
        return updateProject(projectUpdateDto)
          .then((updatedProject) => {
            setProject(updatedProject)
          })
          .then((newProject) => {
            refreshAllProjectCard()
            return newProject
          })
      } else {
        return Promise.resolve()
      }
    },
    [projectId, refreshAllProjectCard, updateProject]
  )

  const refreshProject: () => Promise<Project | undefined> = useCallback(() => {
    if (projectId) {
      return getProject(projectId).then((fetchedProject) => {
        if (fetchedProject) {
          setProject(fetchedProject)
        }
        return fetchedProject
      })
    } else {
      setProject(undefined)
      return Promise.resolve(undefined)
    }
  }, [projectId, getProject])

  useEffect(() => {
    refreshProject()
  }, [refreshProject])

  const handleUpdateProjectStatus: (status: ProjectStatusEnum) => Promise<void> = useCallback(
    (status: ProjectStatusEnum) => {
      if (project?.id) {
        return updateProjectStatus(project.id, status)
          .then((updatedProject) => {
            if (updatedProject) {
              setProject(updatedProject)
            }
          })
          .then(() => refreshAllProjectCard())
          .then(() => openSuccessSnackbar("Le statut du projet a bien été modifié")) // Refresh organization to refresh the projects counter
      }
      return Promise.resolve()
    },
    [project?.id, updateProjectStatus, refreshAllProjectCard, openSuccessSnackbar]
  )

  const handleDeleteProject: () => Promise<void> = useCallback(() => {
    if (project?.id && !isDeleting) {
      setIsDeleting(true)
      return updateProjectStatus(project.id, ProjectStatusEnum.DELETED)
        .then((deletedProject) => {
          if (deletedProject) {
            setProject(deletedProject)
          }
        })
        .then(() => refreshOrganization()) // Refresh organization to refresh the projects counter
        .then(() => refreshAllProjectCard())
        .then(() => openSuccessSnackbar("Suppression du projet réalisé avec succès"))
        .finally(() => {
          setIsDeleting(false)
        })
    }
    return Promise.resolve()
  }, [project?.id, isDeleting, updateProjectStatus, refreshOrganization, refreshAllProjectCard, openSuccessSnackbar])

  const projectStore: ProjectStore = useMemo(
    () => ({
      project,
      setProject,
      createNewProject,
      refreshProject,
      fetchProject,
      updateActualProject,
      handleDeleteProject,
      handleUpdateProjectStatus,
    }),
    [
      project,
      createNewProject,
      refreshProject,
      fetchProject,
      updateActualProject,
      handleDeleteProject,
      handleUpdateProjectStatus,
    ]
  )

  return (
    <ProjectContext.Provider value={projectStore}>
      <CodeReferenceContextProvider projectCodeVersion={project?.codesVersion ?? CodesVersion.VERSION_1_6}>
        <ProjectCountersContextProvider project={project}>{children}</ProjectCountersContextProvider>
      </CodeReferenceContextProvider>
    </ProjectContext.Provider>
  )
}

export interface ProjectStore {
  project: Project | undefined
  setProject: Dispatch<SetStateAction<Project | undefined>>

  createNewProject(projectCreationDto: ProjectCreationDto): Promise<Project>
  refreshProject(): Promise<Project | undefined>
  fetchProject(projectId: string): Promise<void>
  updateActualProject(projectUpdateDto: ProjectUpdateDto): Promise<void>
  handleDeleteProject(): Promise<void>
  handleUpdateProjectStatus(status: ProjectStatusEnum): Promise<void>
}
